// public/js/features/devices/components/device-detail-modal.js
// Read-only detail modal for a device. Every value is click-to-copy (click the
// value or the copy icon). Known fields are formatted nicely (MAC, voltages,
// temperatures, timestamps, byte sizes, …).
import { formatLocalTime } from '../../../utils/asset-utils.js';
import { deviceTypeLabel, vendorModel } from '../device-type.js';
import { convertValue } from '../../../utils/units.js';
import { estimateRemaining, formatRemaining } from '../../tracker-config/battery-usage.js';
import { batteryRemainingPill, wireBatteryHelp } from '../../tracker-config/battery-remaining-ui.js';

let modalInstance = null;
let currentDevice = null;

// Prominent identifier fields (shown first, only when present & non-null).
const KEY_FIELDS = [
  ['deviceId', 'Device ID'],
  ['serialNumber', 'Serial / ESN'],
  ['imei', 'IMEI'],
  ['iccid', 'ICCID'],
  ['mac', 'MAC address'],
  ['uuid', 'iBeacon UUID'],
  ['major', 'Major'],
  ['minor', 'Minor'],
  ['namespace', 'Eddystone namespace'],
  ['instance', 'Eddystone instance'],
  ['gatewayDeviceId', 'Gateway device ID'],
  ['assetUUID', 'Assigned asset UUID'],
];

const LABELS = {
  lastSeen: 'Last seen', lastLat: 'Last latitude', lastLon: 'Last longitude',
  lat: 'Latitude', lon: 'Longitude', timestamp: 'Timestamp', gpsUtc: 'GPS UTC',
  updatedAt: 'Updated at', createdAt: 'Created at',
  battery: 'Battery', batteryPercent: 'Battery %', loadedVoltage: 'Loaded voltage',
  externalVoltage: 'External voltage', temperature: 'Temperature', humidity: 'Humidity',
  rssi: 'RSSI', speed: 'Speed', heading: 'Heading', altitude: 'Altitude',
  posAcc: 'Position accuracy', accuracy: 'Accuracy', pdop: 'PDOP', spdAcc: 'Speed accuracy',
  gsmSignal: 'GSM signal', fw: 'Firmware', prodId: 'Product ID', recordCount: 'Record count',
  recordReason: 'Record reason', logReason: 'Log reason', subType: 'Sub-type',
  tagReason: 'Tag reason', txPower: 'TX power', digitalInputs: 'Digital inputs', inFlight: 'In flight', flightChangedAt: 'Flight state since',
  digitalOutputs: 'Digital outputs', deviceStatus: 'Device status', gpsStat: 'GPS status',
  fixOk: 'Fix OK', lastFixFailed: 'Last fix failed', tamper: 'Tamper',
  batteryGood: 'Battery good', externalPowerGood: 'External power good',
  format: 'Format', source: 'Source', tagType: 'Tag type', pressure: 'Pressure',
  fuelLevel: 'Fuel level', probe1Temp: 'Probe 1 temp', probe2Temp: 'Probe 2 temp',
};

// Keys handled elsewhere or not worth showing as a row.
const SKIP = new Set(['type', 'version', 'tenantName', 'metadata', ...KEY_FIELDS.map(k => k[0])]);

export function createDeviceDetailModal() {
  if (document.getElementById('deviceDetailModal')) return;
  const html = `
    <div class="modal fade" id="deviceDetailModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fa-solid fa-satellite-dish me-2"></i><span id="device-detail-title">Device</span></h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" id="device-detail-body"></div>
          <div class="modal-footer">
            <button type="button" id="device-copy-json" class="btn btn-outline-secondary btn-sm me-auto">
              <i class="fa-regular fa-copy me-1"></i>Copy raw JSON
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);
  const modalEl = document.getElementById('deviceDetailModal');
  modalInstance = new bootstrap.Modal(modalEl);
  document.getElementById('device-copy-json').addEventListener('click', () => {
    if (currentDevice) copyWithFeedback(JSON.stringify(currentDevice, null, 2), document.getElementById('device-copy-json'));
  });
}

export function openDeviceDetailModal(device) {
  if (!device) return;
  if (!document.getElementById('deviceDetailModal')) createDeviceDetailModal();
  currentDevice = device;

  document.getElementById('device-detail-title').textContent =
    device.deviceId || device.serialNumber || 'Device';

  const body = document.getElementById('device-detail-body');
  body.innerHTML = renderBody(device);
  wireBatteryHelp(body, [device]);
  attachCopy(body);
  modalInstance.show();
}

function renderBody(device) {
  // 1) Identifiers
  const idRows = KEY_FIELDS
    .filter(([k]) => present(device[k]))
    .map(([k, label]) => copyRow(label, String(device[k]), String(device[k])))
    .join('');

  // Tenant (display name, copy id) + assignment state.
  let metaRows = '';
  if (present(device.tenantId)) {
    metaRows += copyRow('Tenant', device.tenantName || device.tenantId, device.tenantId, 'tenant-name');
  }
  metaRows += staticRow('Type', deviceTypeLabel(device));
  // Exact vendor model - shown ONLY here (the devices detail, itself gated to the
  // listDevices role). Customers never see it anywhere else; the branded name is
  // used everywhere customer-facing. See device-type.js vendorModel().
  const exactModel = vendorModel(device);
  if (exactModel) metaRows += staticRow('Model', exactModel);
  metaRows += staticRow('Assignment', device.assetUUID
    ? '<span class="badge bg-success">Assigned</span>'
    : '<span class="badge bg-secondary">Unassigned</span>');

  // Battery accounting (docs/battery-remaining.md): its own section, and the
  // raw batt* attributes are kept out of the generic list below - they are the
  // accounting's working state, not something to read row by row.
  let batterySection = '';
  const est = estimateRemaining(device);
  const pill = batteryRemainingPill(device);
  if (est && pill) {
    const rows =
      staticRow('Remaining', pill) +
      staticRow('Used', `${est.consumedMAh.toFixed(0)} of ${est.usableMAh.toFixed(0)} mAh (${est.consumedPct.toFixed(1)} %)`) +
      staticRow('Rate', `${est.ratePerDay.toFixed(2)} mAh/day <span class="text-muted">(${est.rateSource === 'window30' ? 'last 30 days' : 'lifetime average'})</span>`) +
      staticRow('Counting since', `${formatLocalTime(est.epochAt)} <span class="text-muted">(${est.epochKind === 'firstContact' ? 'first contact' : est.epochKind}, ${est.uplinks} uplinks, model v${est.modelVersion})</span>`) +
      (est.notes.length ? staticRow('Notes', est.notes.map(n => `<div class="small text-muted">${n}</div>`).join('')) : '');
    batterySection = section('Battery estimate', rows);
  }

  // 2) Details (everything else, formatted)
  const detailRows = Object.keys(device)
    .filter(k => !SKIP.has(k) && k !== 'tenantId' && !/^batt[A-Z]/.test(k) && present(device[k]) && typeof device[k] !== 'object')
    .map(k => {
      const { display, copy } = formatValue(k, device[k]);
      return copyRow(LABELS[k] || humanize(k), display, copy);
    })
    .join('');

  // 3) Metadata (nested scalar entries)
  let metaSection = '';
  if (device.metadata && typeof device.metadata === 'object') {
    const rows = Object.keys(device.metadata)
      .filter(k => present(device.metadata[k]) && typeof device.metadata[k] !== 'object')
      .map(k => {
        const { display, copy } = formatValue(k, device.metadata[k]);
        return copyRow(LABELS[k] || humanize(k), display, copy);
      }).join('');
    if (rows) metaSection = section('Metadata', rows);
  }

  return `
    ${idRows ? section('Identifiers', idRows) : ''}
    ${batterySection}
    ${section('Status', metaRows)}
    ${detailRows ? section('Telemetry & details', detailRows) : ''}
    ${metaSection}
  `;
}

function section(title, rowsHtml) {
  return `
    <div class="mb-3">
      <div class="text-uppercase text-muted fw-bold mb-1" style="font-size:0.7rem; letter-spacing:0.08em;">${title}</div>
      <div class="border rounded">${rowsHtml}</div>
    </div>`;
}

function copyRow(label, display, copy, displayClass = '') {
  return `
    <div class="d-flex justify-content-between align-items-center gap-3 px-2 py-2 border-bottom device-copy"
         data-copy="${escapeAttr(copy)}" role="button" title="Click to copy">
      <span class="text-muted small flex-shrink-0">${escapeHtml(label)}</span>
      <span class="d-flex align-items-center gap-2 text-end" style="min-width:0;">
        <span class="fw-semibold ${displayClass}" style="word-break:break-all;">${escapeHtml(display)}</span>
        <i class="fa-regular fa-copy text-secondary copy-ic flex-shrink-0"></i>
      </span>
    </div>`;
}

function staticRow(label, displayHtml) {
  return `
    <div class="d-flex justify-content-between align-items-center gap-3 px-2 py-2 border-bottom">
      <span class="text-muted small">${escapeHtml(label)}</span>
      <span class="fw-semibold text-end">${displayHtml}</span>
    </div>`;
}

// ── value formatting ──────────────────────────────────────────────────────────
const TS_KEYS = new Set(['timestamp', 'lastSeen', 'gpsUtc', 'updatedAt', 'createdAt']);

function formatValue(key, value) {
  if (value === true || value === false) return { display: value ? 'Yes' : 'No', copy: String(value) };
  if (TS_KEYS.has(key)) return { display: formatLocalTime(value), copy: String(value) };
  if (key === 'mac') { const m = formatMac(value); return { display: m, copy: m }; }

  const num = typeof value === 'number' ? value
    : (value !== '' && value != null && !isNaN(value) ? Number(value) : null);

  if (num !== null) {
    const r = (d) => String(Math.round(num * 10 ** d) / 10 ** d);
    // Honour the user's unit system for convertible quantities (°C, km/h, m).
    const uconv = (metricUnit, d) => { const c = convertValue(num, metricUnit, d); return unit(String(c.value), c.unit); };
    if (key === 'temperature' || key === 'probe1Temp' || key === 'probe2Temp') return uconv('°C', 2);
    if (key === 'humidity') return unit(r(1), '%');
    if (key === 'batteryPercent') return unit(r(0), '%');
    if (['battery', 'loadedVoltage', 'externalVoltage'].includes(key)) return unit(r(3), 'V');
    if (key === 'rssi' || key === 'txPower') return unit(r(0), 'dBm');
    if (key === 'rssiAvg' || key === 'rssiFloor') return unit(r(1), 'dBm');
    if (key === 'rssiSpread') return unit(r(1), 'dB');
    if (key === 'speed') return uconv('km/h', 1);
    if (key === 'heading') return { display: `${r(0)}°`, copy: r(0) };
    if (['altitude', 'posAcc', 'accuracy', 'spdAcc'].includes(key)) return uconv('m', 0);
    if (['lat', 'lon', 'lastLat', 'lastLon'].includes(key)) return { display: r(6), copy: r(6) };
    if (key === 'pressure') return unit(r(1), 'hPa');
    if (/bytes|size/i.test(key)) return { display: formatBytes(num), copy: String(num) };
    return { display: String(value), copy: String(value) };
  }
  return { display: String(value), copy: String(value) };
}

const unit = (v, u) => ({ display: `${v} ${u}`, copy: v });

function formatMac(v) {
  const hex = String(v).replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  const pairs = hex.match(/.{1,2}/g);
  return pairs ? pairs.join(':') : String(v);
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return String(n);
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, x = n;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
  return `${i === 0 ? x : x.toFixed(2)} ${units[i]}`;
}

// ── copy interaction ──────────────────────────────────────────────────────────
function attachCopy(root) {
  root.querySelectorAll('.device-copy').forEach(el => {
    el.addEventListener('click', () => {
      copyWithFeedback(el.getAttribute('data-copy'), el);
    });
  });
}

async function copyWithFeedback(text, el) {
  const ok = await copyText(text);
  const icon = el.querySelector('.copy-ic') || el.querySelector('i');
  if (icon) {
    const prev = icon.className;
    icon.className = `fa-solid fa-check text-success ${icon.classList.contains('copy-ic') ? 'copy-ic' : ''}`;
    setTimeout(() => { icon.className = prev; }, 1200);
  }
  window.showToast?.(ok ? 'Copied to clipboard' : 'Copy failed', ok ? 'success' : 'danger');
}

async function copyText(t) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(t); return true; }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = t; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    const ok = document.execCommand('copy'); ta.remove(); return ok;
  } catch (e) { return false; }
}

// ── helpers ───────────────────────────────────────────────────────────────────
function present(v) { return v != null && v !== ''; }
function humanize(k) {
  return k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
}
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
